import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { Plus, MapPin, Check, Camera } from "lucide-react";
import { toast } from "sonner";

export default function Visits() {
  const [tab, setTab] = useState("ALL");
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ customer_id: "", scheduled_at: "", purpose: "" });

  const load = async () => {
    const params = tab === "ALL" ? {} : { status: tab };
    const { data } = await api.get("/sales/visits", { params });
    setItems(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);
  useEffect(() => { api.get("/sales/customers").then(({ data }) => setCustomers(data)); }, []);

  const save = async () => { try { await api.post("/sales/visits", form); toast.success("Visit scheduled"); setOpen(false); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };

  const capturePos = () => new Promise((resolve) => {
    if (!navigator.geolocation) return resolve({});
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude, accuracy: pos.coords.accuracy }),
      () => resolve({}),
      { timeout: 8000, enableHighAccuracy: true }
    );
  });

  const checkin = async (v) => {
    const pos = await capturePos();
    try { await api.post(`/sales/visits/${v.id}/checkin`, pos); toast.success("Checked in"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const checkout = async (v) => {
    const pos = await capturePos();
    try { await api.post(`/sales/visits/${v.id}/checkout`, pos); toast.success("Checked out"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6" data-testid="sales-visits">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-black">Site visits</h1>
          <p className="text-muted-foreground">Schedule visits, GPS check-in/out, capture site photos and add notes from the field.</p>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="visit-create-btn"><Plus className="w-4 h-4 mr-1" /> Schedule</Button>
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="ALL" data-testid="visit-tab-ALL">All</TabsTrigger>
          {["PLANNED", "IN_PROGRESS", "COMPLETED", "MISSED"].map((s) => <TabsTrigger key={s} value={s} data-testid={`visit-tab-${s}`}>{s}</TabsTrigger>)}
        </TabsList>
      </Tabs>
      <div className="grid md:grid-cols-2 gap-3">
        {items.map((v) => (
          <Card key={v.id} data-testid={`visit-card-${v.id}`}>
            <CardContent className="p-4 space-y-2">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-semibold">{customers.find((c) => c.id === v.customer_id)?.name || v.lead_id || "Visit"}</div>
                  <div className="text-xs text-muted-foreground">{v.purpose || "—"}</div>
                </div>
                <Badge variant="outline" className="font-mono text-[10px]">{v.status}</Badge>
              </div>
              <div className="font-mono text-[10px] text-muted-foreground">Scheduled: {v.scheduled_at ? new Date(v.scheduled_at).toLocaleString() : "—"}</div>
              {v.checkin && <div className="text-[11px]"><MapPin className="w-3 h-3 inline" /> Check-in @ {new Date(v.checkin.at).toLocaleString()} {v.checkin.latitude ? `(${v.checkin.latitude.toFixed(3)}, ${v.checkin.longitude.toFixed(3)})` : ""}</div>}
              {v.checkout && <div className="text-[11px]"><Check className="w-3 h-3 inline" /> Check-out @ {new Date(v.checkout.at).toLocaleString()}</div>}
              <div className="flex gap-2 pt-2">
                {v.status === "PLANNED" && <Button size="sm" onClick={() => checkin(v)} data-testid={`visit-checkin-${v.id}`}><MapPin className="w-4 h-4 mr-1" /> Check in</Button>}
                {v.status === "IN_PROGRESS" && <Button size="sm" onClick={() => checkout(v)} data-testid={`visit-checkout-${v.id}`}><Check className="w-4 h-4 mr-1" /> Check out</Button>}
                <Button size="sm" variant="outline" title="Upload photo (UI only)"><Camera className="w-4 h-4 mr-1" /> Photo</Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {items.length === 0 && <div className="text-center text-muted-foreground py-10 col-span-2">No visits scheduled.</div>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Schedule visit</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Customer</Label>
              <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v })}>
                <SelectTrigger data-testid="visit-customer"><SelectValue placeholder="Pick customer…" /></SelectTrigger>
                <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Scheduled at</Label><Input type="datetime-local" value={form.scheduled_at} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })} data-testid="visit-time" /></div>
            <div><Label>Purpose</Label><Input value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} data-testid="visit-purpose" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} data-testid="visit-save">Schedule</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
